"use client";

// app/error.tsx
//
// Route-level error boundary. Next.js renders this when a server component
// or client component in the current route segment throws an unhandled error.
// It sits inside the root layout (so the navbar stays visible) and lets the
// user retry without a full page reload.
//
// We report to the server on mount so the error is logged and the admin is
// emailed even if the user just closes the tab.

import { useEffect, useCallback } from "react";
import { usePathname } from "next/navigation";
import { reportClientBoundaryError } from "./actions/error-report";

export default function ErrorBoundary({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const pathname = usePathname();

  useEffect(() => {
    // Report once on mount — fire-and-forget from the client's perspective
    reportClientBoundaryError({
      message:  error.message,
      stack:    error.stack,
      digest:   error.digest,
      pathname: pathname ?? "unknown",
    }).catch(() => {
      // Swallow silently — if the server action fails we still show the UI
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleReset = useCallback(() => {
    reset();
  }, [reset]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <div className="text-5xl mb-4">⚠️</div>
        <h1 className="text-2xl font-black text-purple-800 mb-3">
          Something went wrong
        </h1>
        <p className="text-purple-500 text-sm mb-2">
          {error.digest
            ? `Error ID: ${error.digest}`
            : "An unexpected error occurred on this page."}
        </p>
        <p className="text-purple-400 text-xs mb-6">
          The admins have been notified automatically.
        </p>
        <button
          onClick={handleReset}
          className="rounded-xl bg-purple-700 hover:bg-purple-600 text-white font-bold px-6 py-3 transition-colors"
        >
          Try again
        </button>
      </div>
    </div>
  );
}
